import { useRef, useState, type ReactNode } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faListCheck,
  faComment,
  faArrowUp,
} from '@fortawesome/pro-regular-svg-icons'
import Typography from '@mui/material/Typography'
import { useLabraChat, LoadingStateComponent } from '@labrav/common'
import { FundingTodo } from '../../types'
import { ScrollHint } from '../ScrollHint/ScrollHint'
import { useStyles } from './FundingToDos.styles'

interface FundingToDosProps {
  todos?: FundingTodo[]
  loading?: boolean
  /** Card heading. Defaults to "ToDos". */
  title?: string
  /**
   * Optional control rendered on the heading row (right-aligned), e.g. an
   * "Ask Labra AI" entry point on Salesforce Canvas.
   */
  headerAction?: ReactNode
  /**
   * Override for what a tile click / send-box submit does with the prompt.
   * When provided, the in-panel chat is bypassed and the caller receives the
   * raw text instead — used on Salesforce Canvas, where the chat lives in a
   * separate iframe and is opened via postMessage rather than the local panel.
   */
  onSendPrompt?: (text: string) => void
  /**
   * Intercepts a tile click before the chat is opened. Return `true` to signal
   * the click was fully handled (e.g. a host action fired) so the chat is
   * skipped; return `false` to fall through to the normal send behaviour.
   */
  onTodoClick?: (todo: FundingTodo) => boolean
}

const INPUT_PLACEHOLDER = 'Ask AI to help with todos'

export const FundingToDos = ({
  todos = [],
  loading = false,
  title = 'ToDos',
  headerAction,
  onSendPrompt,
  onTodoClick,
}: FundingToDosProps) => {
  const { classes } = useStyles()
  const { open, startNewChat, sendPrompt } = useLabraChat()
  const [value, setValue] = useState('')
  const rowsRef = useRef<HTMLDivElement>(null)

  const askAgent = (prompt: string) => {
    const trimmed = prompt.trim()
    if (!trimmed) return
    if (onSendPrompt) {
      onSendPrompt(trimmed)
      return
    }
    open()
    startNewChat()
    sendPrompt(trimmed)
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    askAgent(value)
    setValue('')
  }

  return (
    <section className={classes.card} data-testid="funding-todos">
      <div className={classes.body}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <span className={classes.headerIcon}>
              <FontAwesomeIcon icon={faListCheck} />
            </span>
            <Typography component="h2" className={classes.title}>
              {title}
            </Typography>
          </div>
          {headerAction ? (
            <div className={classes.headerAction}>{headerAction}</div>
          ) : null}
        </div>

        {loading ? (
          <div className={classes.loading}>
            <LoadingStateComponent
              heading="Fetching your todos"
              description="It may take a few moments, please wait..."
            />
          </div>
        ) : (
          todos.length > 0 && (
            <div className={classes.scrollWrap}>
              <div ref={rowsRef} className={classes.rows}>
                {todos.map(todo => (
                  <button
                    key={todo.id}
                    type="button"
                    className={classes.row}
                    data-testid={`funding-todo-${todo.id}`}
                    onClick={() => {
                      if (onTodoClick?.(todo)) return
                      askAgent(todo.prompt ?? todo.text)
                    }}
                  >
                    <span className={classes.rowIcon}>
                      <FontAwesomeIcon icon={faComment} />
                    </span>
                    <Typography component="span" className={classes.rowText}>
                      {todo.text}
                    </Typography>
                  </button>
                ))}
              </div>
              <ScrollHint scrollRef={rowsRef} contentKey={todos.length} />
            </div>
          )
        )}
      </div>

      <form className={classes.pill} onSubmit={handleSubmit}>
        <input
          className={classes.input}
          placeholder={INPUT_PLACEHOLDER}
          value={value}
          onChange={event => setValue(event.target.value)}
        />
        <button type="submit" className={classes.pillButton} aria-label="Send">
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </form>
    </section>
  )
}

export default FundingToDos
